import {
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import { SparePart } from "../types";

interface SparePartDetailsDialogProps {
  open: boolean;
  sparePart: SparePart | null;
  onClose: () => void;
}

const Detail = ({ label, value }: { label: string; value?: string | number | null }) => (
  <Stack spacing={0.5}>
    <Typography variant="caption" color="text.secondary" fontWeight={800}>
      {label.toUpperCase()}
    </Typography>
    <Typography fontWeight={700}>{value ?? "-"}</Typography>
  </Stack>
);

const SparePartDetailsDialog = ({
  open,
  sparePart,
  onClose,
}: SparePartDetailsDialogProps) => {
  if (!sparePart) return null;

  const currentStock = Number(sparePart.currentStock || 0);
  const reorderLevel = Number(sparePart.reorderLevel || 0);
  const stockStatus =
    currentStock === 0
      ? "Out Of Stock"
      : currentStock <= reorderLevel
        ? "Low Stock"
        : "In Stock";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack>
            <Typography variant="h6" fontWeight={900}>
              {sparePart.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {sparePart.code} - {sparePart.categoryName || "-"}
            </Typography>
          </Stack>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Detail label="Part Code" value={sparePart.code} />
          </Grid>
          <Grid item xs={12} md={4}>
            <Detail label="Category" value={sparePart.categoryName} />
          </Grid>
          <Grid item xs={12} md={4}>
            <Stack spacing={0.5}>
              <Typography variant="caption" color="text.secondary" fontWeight={800}>
                STATUS
              </Typography>
              <Chip
                size="small"
                color={sparePart.status === "ACTIVE" ? "success" : "default"}
                label={sparePart.status}
                sx={{ width: "fit-content", fontWeight: 800 }}
              />
            </Stack>
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          <Grid item xs={6} md={3}>
            <Detail label="Total Hub Stock" value={currentStock} />
          </Grid>
          <Grid item xs={6} md={3}>
            <Detail label="Stock Status" value={stockStatus} />
          </Grid>
          <Grid item xs={6} md={3}>
            <Detail label="Minimum Stock" value={sparePart.minimumStock} />
          </Grid>
          <Grid item xs={6} md={3}>
            <Detail label="Reorder Level" value={sparePart.reorderLevel} />
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle1" fontWeight={900} sx={{ mb: 1.5 }}>
              Hub-Wise Availability
            </Typography>

            {sparePart.hubStocks?.length ? (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Hub</TableCell>
                    <TableCell align="right">Available</TableCell>
                    <TableCell align="right">Reserved</TableCell>
                    <TableCell align="right">Damaged</TableCell>
                    <TableCell align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sparePart.hubStocks.map((stock) => (
                    <TableRow key={stock.warehouseId}>
                      <TableCell>{stock.warehouseName}</TableCell>
                      <TableCell align="right">{stock.availableStock}</TableCell>
                      <TableCell align="right">{stock.reservedStock}</TableCell>
                      <TableCell align="right">{stock.damagedStock}</TableCell>
                      <TableCell align="right">{stock.totalStock}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <Typography color="text.secondary">
                No inventory has been received or adjusted for this spare in any hub yet.
              </Typography>
            )}
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          <Grid item xs={6} md={3}>
            <Detail label="Maximum Stock" value={sparePart.maximumStock ?? "-"} />
          </Grid>
          <Grid item xs={6} md={3}>
            <Detail label="Warranty Months" value={sparePart.warrantyMonths ?? "-"} />
          </Grid>
          <Grid item xs={6} md={3}>
            <Detail
              label="Maximum Per Service Issue"
              value={sparePart.maxQuantityPerServiceIssue ?? "No limit"}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <Detail
              label="Created"
              value={new Date(sparePart.createdAt).toLocaleString()}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <Detail
              label="Updated"
              value={new Date(sparePart.updatedAt).toLocaleString()}
            />
          </Grid>

          {(sparePart.description || sparePart.remarks || sparePart.imageUrl) && (
            <Grid item xs={12}>
              <Divider />
            </Grid>
          )}

          {sparePart.description && (
            <Grid item xs={12}>
              <Detail label="Description" value={sparePart.description} />
            </Grid>
          )}

          {sparePart.remarks && (
            <Grid item xs={12}>
              <Detail label="Remarks" value={sparePart.remarks} />
            </Grid>
          )}

          {sparePart.imageUrl && (
            <Grid item xs={12}>
              <Detail label="Image Url" value={sparePart.imageUrl} />
            </Grid>
          )}
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default SparePartDetailsDialog;
